import React, { useEffect, useState } from "react";
import api from "../../api";
import Loader from "../../components/Loader";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

const revenueData = [
  { month: "Jan", subscriptions: 40, addons: 20 },
  { month: "Feb", subscriptions: 50, addons: 20 },
  { month: "Mar", subscriptions: 70, addons: 30 },
  { month: "Apr", subscriptions: 80, addons: 40 },
  { month: "May", subscriptions: 120, addons: 60 },
  { month: "Jun", subscriptions: 150, addons: 80 },
  { month: "Jul", subscriptions: 100, addons: 50 },
];

const activityData = [
  { month: "Jan", activity: 20 },
  { month: "Feb", activity: 30 },
  { month: "Mar", activity: 45 },
  { month: "Apr", activity: 55 },
  { month: "May", activity: 60 },
  { month: "Jun", activity: 70 },
  { month: "Jul", activity: 50 },
];

export default function Dashboard({ user: propUser }) {
  const user = propUser || JSON.parse(localStorage.getItem("user"));
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    activeStaff: 0,
    totalKitties: 0,
    activeKitties: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const customersRes = await api.get("/jeweller-customers");
        const totalCustomers = customersRes.data.length;

        const staffRes = await api.get("/staff");
        const activeStaff = staffRes.data.filter((s) => s.status === "Active").length;

        const kittiesRes = await api.get("/kitties");
        const totalKitties = kittiesRes.data.length;
        const activeKitties = kittiesRes.data.filter((k) => k.status === "active").length;

        setStats({ totalCustomers, activeStaff, totalKitties, activeKitties });
      } catch (err) {
        console.error("Error fetching dashboard data:", err.response || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <Loader />;

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-800 mb-2">
          Welcome back,{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500">
            {user?.name ? user.name.charAt(0).toUpperCase() + user.name.slice(1) : "User"}
          </span>
          👋
        </h1>
        <p className="text-gray-500">Here’s an overview of your business performance.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <StatCard
          title="Total Customers"
          value={stats.totalCustomers}
          gradient="from-blue-500 to-blue-700"
          icon="👥"
        />
        <StatCard
          title="Active Staff"
          value={stats.activeStaff}
          gradient="from-emerald-500 to-green-600"
          icon="💼"
        />
        <StatCard
          title="Total Kitties"
          value={stats.totalKitties}
          gradient="from-purple-500 to-indigo-600"
          icon="💎"
        />
        <StatCard
          title="Active Kitties"
          value={stats.activeKitties}
          gradient="from-pink-500 to-rose-600"
          icon="🔥"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Revenue Trends */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            📈 Revenue Trends
          </h2>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={revenueData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip />
              <Line type="monotone" dataKey="subscriptions" stroke="#9333EA" strokeWidth={2.5} />
              <Line type="monotone" dataKey="addons" stroke="#F59E0B" strokeWidth={2.5} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Activity Logs */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            📊 Activity Logs
          </h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={activityData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip />
              <Bar dataKey="activity" fill="#8B5CF6" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

/* 📦 Reusable Stat Card Component */
function StatCard({ title, value, gradient, icon }) {
  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5 hover:shadow-lg transition-all duration-300">
      <div
        className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white text-xl mb-3`}
      >
        {icon}
      </div>
      <p className="text-sm text-gray-500">{title}</p>
      <p className="text-2xl font-semibold text-gray-800 mt-1">{value}</p>
    </div>
  );
}
